/**
 * Storage quota checking utility
 * Checks available storage before operations to prevent QuotaExceededError
 */

/**
 * Check if there's enough storage space available
 * @param {number} requiredBytes - Bytes needed for the operation
 * @returns {Promise<{hasSpace: boolean, available: number, quota: number, usage: number, message: string}>}
 */
export const checkStorageQuota = async (requiredBytes = 0) => {
  try {
    // Check if Storage API is available
    if (!navigator.storage || !navigator.storage.estimate) {
      // Fallback: assume we have space if API not available
      return {
        hasSpace: true,
        available: Infinity,
        quota: Infinity,
        usage: 0,
        message: 'Storage API not available'
      };
    }

    const estimate = await navigator.storage.estimate();
    const quota = estimate.quota || 0;
    const usage = estimate.usage || 0;
    const available = quota - usage;
    const hasSpace = available >= requiredBytes;

    // Detect browser for better messaging
    const isSafariMobile = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && 
                           /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isChrome = /chrome/i.test(navigator.userAgent) && !/edge/i.test(navigator.userAgent);
    const isAndroid = /android/i.test(navigator.userAgent);

    let message = '';
    if (!hasSpace) {
      const requiredMB = (requiredBytes / (1024 * 1024)).toFixed(2);
      const availableMB = (available / (1024 * 1024)).toFixed(2);
      
      if (isSafariMobile) {
        message = `Not enough storage space. Need ${requiredMB}MB but only ${availableMB}MB available. Please sync your data or clear browser storage in Settings > Safari > Clear History and Website Data.`;
      } else if (isChrome && isAndroid) {
        message = `Not enough storage space. Need ${requiredMB}MB but only ${availableMB}MB available. Please sync your data or clear browser storage: Chrome Menu > Settings > Privacy and Security > Clear browsing data.`;
      } else if (isChrome) {
        message = `Not enough storage space. Need ${requiredMB}MB but only ${availableMB}MB available. Please sync your data or clear browser storage: Chrome Menu > Settings > Privacy and Security > Clear browsing data > Cached images and files.`;
      } else {
        message = `Not enough storage space. Need ${requiredMB}MB but only ${availableMB}MB available. Please sync your data or clear browser storage/cache.`;
      }
    }

    return {
      hasSpace,
      available,
      quota,
      usage,
      message,
      usagePercent: quota > 0 ? ((usage / quota) * 100).toFixed(1) : 0
    };
  } catch (error) {
    console.error('Error checking storage quota:', error);
    // If check fails, assume we have space (better to try than block)
    return {
      hasSpace: true,
      available: Infinity,
      quota: Infinity,
      usage: 0,
      message: 'Could not check storage quota'
    };
  }
};

/**
 * Estimate size of image data for storage check
 * @param {File|Blob|ArrayBuffer|Object} imageData - Image data
 * @returns {number} Estimated size in bytes
 */
export const estimateImageSize = (imageData) => {
  if (!imageData) return 0;
  
  if (imageData instanceof File || imageData instanceof Blob) {
    return imageData.size;
  }
  
  if (imageData instanceof ArrayBuffer) {
    return imageData.byteLength;
  }
  
  if (imageData.data && imageData.data instanceof ArrayBuffer) {
    return imageData.data.byteLength;
  }
  
  // Fallback: estimate based on type
  if (typeof imageData === 'object' && imageData.type) {
    // Rough estimate: 2MB per image (conservative)
    return 2 * 1024 * 1024;
  }
  
  return 0;
};

/**
 * Check storage before saving model (typically 10-50MB)
 * @returns {Promise<{hasSpace: boolean, message: string}>}
 */
export const checkStorageForModel = async () => {
  const modelSizeEstimate = 50 * 1024 * 1024; // 50MB estimate
  const result = await checkStorageQuota(modelSizeEstimate);
  
  return {
    hasSpace: result.hasSpace,
    message: result.message || 'Storage check passed'
  };
};

/**
 * Check storage before saving images (typically 2-10MB per report)
 * @param {Array} images - Array of image files/blobs
 * @returns {Promise<{hasSpace: boolean, message: string}>}
 */
export const checkStorageForImages = async (images = []) => {
  let totalSize = 0;
  
  images.forEach(image => {
    totalSize += estimateImageSize(image);
  });
  
  // Add 20% buffer for overhead
  const requiredBytes = Math.ceil(totalSize * 1.2);
  
  const result = await checkStorageQuota(requiredBytes);
  
  return {
    hasSpace: result.hasSpace,
    message: result.message || 'Storage check passed',
    estimatedSize: totalSize
  };
};

/**
 * Get storage usage info for display
 * @returns {Promise<{usageMB: string, quotaMB: string, usagePercent: string, availableMB: string}>}
 */
export const getStorageInfo = async () => {
  const result = await checkStorageQuota(0);
  
  return {
    usageMB: (result.usage / (1024 * 1024)).toFixed(2),
    quotaMB: (result.quota / (1024 * 1024)).toFixed(2),
    usagePercent: result.usagePercent,
    availableMB: (result.available / (1024 * 1024)).toFixed(2)
  };
};

